/**
 * EF-EMAIL-SUBJECT-ROUTER-V1 + UI-ZOOM-EXTENSION
 * 
 * Reusable hook for zoom and text wrap state management.
 * Provides persistent zoom levels with localStorage.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';

// Standard zoom levels for all pages
export const ZOOM_LEVELS = [
  { key: "compact", label: "Compact (80%)", scale: 0.80 },
  { key: "normal", label: "Normal (100%)", scale: 1.0 },
  { key: "large", label: "Large (120%)", scale: 1.20 },
];

/**
 * Hook for managing zoom level with localStorage persistence
 * 
 * @param {string} storageKey - localStorage key for this page's zoom preference
 * @param {string} defaultZoom - Default zoom level key (compact, normal, large)
 */
export function useZoom(storageKey, defaultZoom = "normal") {
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && ZOOM_LEVELS.find(z => z.key === saved)) {
        return saved;
      }
    } catch (e) {
      console.warn("Failed to read zoom from localStorage:", e);
    }
    return defaultZoom;
  });

  // Persist zoom changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, zoomLevel);
    } catch (e) {
      console.warn("Failed to save zoom to localStorage:", e);
    }
  }, [storageKey, zoomLevel]);

  // Get current zoom config
  const currentZoom = useMemo(() => {
    return ZOOM_LEVELS.find(z => z.key === zoomLevel) || ZOOM_LEVELS[1];
  }, [zoomLevel]);

  // Zoom in (increase)
  const zoomIn = useCallback(() => {
    const idx = ZOOM_LEVELS.findIndex(z => z.key === zoomLevel);
    if (idx < ZOOM_LEVELS.length - 1) {
      setZoomLevel(ZOOM_LEVELS[idx + 1].key);
    }
  }, [zoomLevel]);

  // Zoom out (decrease)
  const zoomOut = useCallback(() => {
    const idx = ZOOM_LEVELS.findIndex(z => z.key === zoomLevel);
    if (idx > 0) {
      setZoomLevel(ZOOM_LEVELS[idx - 1].key);
    }
  }, [zoomLevel]);

  // Check if at limits
  const canZoomIn = zoomLevel !== ZOOM_LEVELS[ZOOM_LEVELS.length - 1].key;
  const canZoomOut = zoomLevel !== ZOOM_LEVELS[0].key;

  return {
    zoomLevel,
    setZoomLevel,
    currentZoom,
    scale: currentZoom.scale,
    zoomIn,
    zoomOut,
    canZoomIn,
    canZoomOut,
    ZOOM_LEVELS,
  };
}

/**
 * Hook for managing text wrap preference with localStorage persistence
 * 
 * @param {string} storageKey - localStorage key for this page's wrap preference
 * @param {boolean} defaultWrap - Default wrap state
 */
export function useTextWrap(storageKey, defaultWrap = true) {
  const [wrapText, setWrapText] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) {
        return saved === "true";
      }
    } catch (e) {
      console.warn("Failed to read wrap preference from localStorage:", e);
    }
    return defaultWrap;
  });

  // Persist wrap changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(wrapText));
    } catch (e) {
      console.warn("Failed to save wrap preference to localStorage:", e);
    }
  }, [storageKey, wrapText]);

  const toggleWrap = useCallback(() => {
    setWrapText(prev => !prev);
  }, []);

  return {
    wrapText,
    setWrapText,
    toggleWrap,
  };
}

/**
 * Combined hook for zoom + text wrap
 */
export function useTableDisplaySettings(pageKey) {
  const zoom = useZoom(`${pageKey}-zoom`, "normal");
  const wrap = useTextWrap(`${pageKey}-wrap`, true);

  return {
    ...zoom,
    ...wrap,
  };
}

/**
 * CSS classes based on zoom level
 */
export function getZoomClasses(scale) {
  if (scale <= 0.8) {
    return {
      text: "text-xs",
      rowHeight: "h-8",
      cellPadding: "py-1 px-2",
      headerHeight: "h-8",
    };
  } else if (scale >= 1.2) {
    return {
      text: "text-base",
      rowHeight: "h-12",
      cellPadding: "py-3 px-4",
      headerHeight: "h-12",
    };
  } else {
    return {
      text: "text-sm",
      rowHeight: "h-10",
      cellPadding: "py-2 px-3",
      headerHeight: "h-10",
    };
  }
}

export default useZoom;
